/**
 * Île de Braise (GDD section 10), drawn at 1024 × 512: an active volcano. A central cone of rock covered with
 * gravel, a crater full of lava joined to a magma chamber by a conduit, a lava flow down to the sea (an obsidian
 * crust where it meets the water, with steam), old obsidian flows, a forest to the west and a plume of steam.
 * Event (maps/events.ts): an eruption every 4 turns, with a countdown. The seed changes nothing here.
 */
import { Physique, sinDeg, TRIG_ONE } from '@invaders/sim';
import { plainMap, type Box, type MapState } from '../state';
import { type Gen, ellipse, island, layer, rect, rootedTree, sea, settle } from './common';

const { EMPTY, ROCK, SAND, LAVA, STEAM, OBSIDIAN, GRAVEL } = Physique;

/** Centre column of the cone, its height above the sea at the summit, and its radius at the sea level. */
const PEAK_X = 600;
const PEAK = 280;
const RADIUS = 390;
/** Half-width and depth of the crater; the lava stands this far under its rim. */
const CRATER_HALF = 34;
const CRATER_DEPTH = 36;
const LAVA_UNDER_RIM = 7;
/** Width of the crater's rims on the flat top. */
const RIM = 12;
/** Eruptions come every this many turns (GDD). */
export const ERUPTION_EVERY = 4;

/** Small fixed relief (the map is drawn: no randomness). */
const bumps = (x: number) => Math.trunc((5 * sinDeg(x * 3 + 40)) / TRIG_ONE) + Math.trunc((2 * sinDeg(x * 11)) / TRIG_ONE);

export function drawEmber(g: Gen): MapState {
  const { w, seaY, data } = g;
  // A concave cone, cut flat at the top so that the crater's rims hold its lava.
  const profile = (d: number) => (d >= RADIUS ? 0 : Math.trunc((PEAK * (RADIUS - d) * (RADIUS - d + (RADIUS >> 1))) / (RADIUS * (RADIUS + (RADIUS >> 1)))));
  const cone = (x: number) => profile(Math.max(Math.abs(x - PEAK_X), CRATER_HALF + RIM));
  const surface = island(g, 108, 918, 60, (x) => Math.max(46 + bumps(x), cone(x) + (Math.abs(x - PEAK_X) <= CRATER_HALF + RIM ? 0 : bumps(x))));
  const rimY = surface[PEAK_X];

  // The crater: a bowl at the summit, full of lava; the conduit down to the magma chamber, full of lava too.
  const chamberY = seaY - 74;
  ellipse(g, PEAK_X + 10, chamberY, 92, 30, LAVA, ROCK);
  for (let x = PEAK_X - CRATER_HALF; x <= PEAK_X + CRATER_HALF; x++) {
    const d = x - PEAK_X;
    const floor = rimY + Math.trunc((CRATER_DEPTH * (CRATER_HALF * CRATER_HALF - d * d)) / (CRATER_HALF * CRATER_HALF));
    rect(g, x, 0, x, floor, EMPTY);
    rect(g, x, rimY + LAVA_UNDER_RIM, x, floor, LAVA);
  }
  rect(g, PEAK_X - 4, rimY + CRATER_DEPTH - 2, PEAK_X + 4, chamberY, LAVA);

  // Old obsidian flows on the western flank.
  for (const [x0, x1, t] of [[400, 520, 4], [455, 545, 5], [660, 700, 3]]) layer(g, surface, x0, x1, (x) => (Math.min(x - x0, x1 - x) < 6 ? 2 : t), OBSIDIAN);

  // Gravel all over the cone (it does not slide), except in the crater.
  layer(g, surface, PEAK_X - 300, PEAK_X + 300, (x) => (Math.abs(x - PEAK_X) <= CRATER_HALF + RIM ? 0 : 3 + (Math.abs(x - PEAK_X) >> 7)), GRAVEL);

  // The lava flow: a groove down the eastern flank, from a notch in the crater's rim (just over the lava: an
  // eruption overflows there) to the sea, full of lava. Where it meets the water, an obsidian crust and steam.
  const notch = PEAK_X + CRATER_HALF + 1;
  for (let x = notch - 1; x < notch + RIM; x++) rect(g, x, 0, x, rimY + LAVA_UNDER_RIM - 2 + Math.trunc(((x - notch) * 3) / RIM), EMPTY);
  let shore = notch;
  for (let x = notch + RIM; x < w && surface[x] < seaY - 1; x++) {
    let top = 0;
    while (top < g.h && data[top * w + x] === EMPTY) top++;
    rect(g, x, top, x, top + 3, LAVA);
    rect(g, x, top - 1, x, top - 1, EMPTY);
    shore = x;
  }
  for (let x = shore - 6; x <= shore + 14; x++) rect(g, x, seaY - 3, x, seaY + 2, OBSIDIAN, EMPTY);
  for (let y = 120; y < seaY - 6; y++) {
    const half = 6 + Math.trunc(((seaY - y) * 22) / (seaY - 120));
    const cx = shore + 6 + Math.trunc((14 * sinDeg(y * 5)) / TRIG_ONE);
    for (let x = cx - half; x <= cx + half; x++) {
      let hs = Math.imul(x, 0x27d4eb2d) ^ Math.imul(y, 0x165667b1);
      hs = Math.imul(hs ^ (hs >>> 15), 0x85ebca6b);
      if (hs >>> 29 < 5) rect(g, x, y, x, y, STEAM, EMPTY);
    }
  }

  // The forest to the west, on the sand at the foot of the volcano (author's choice), beaches at both ends.
  const rock = surface.slice();
  layer(g, surface, 118, 356, (x) => Math.min(9, Math.trunc((x - 118) / 3), Math.trunc((356 - x) / 3)), SAND);
  for (let x = 140; x < 330; x += 19 + ((x * 7) % 9)) rootedTree(g, x, rock[x], surface[x], 24 + ((x * 13) % 16));
  layer(g, surface, 50, 150, (x) => Math.max(0, 5 - Math.trunc(Math.abs(x - 96) / 10)), SAND);
  layer(g, surface, 900, 990, (x) => Math.max(0, 5 - Math.trunc(Math.abs(x - 938) / 10)), SAND);

  sea(g);
  settle(g);
  const crater: Box = { x0: PEAK_X - CRATER_HALF, y0: rimY - 2, x1: PEAK_X + CRATER_HALF, y1: rimY + CRATER_DEPTH };
  return { ...plainMap('ember'), crater, eruptionIn: ERUPTION_EVERY };
}
